import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {
    MultiPageVideoModel,
    ElementTypes,
    VideoElement,
    AudioElement,
    SpecificationExporter,
    srtTimecodeToFrame,
    type TranscriptCue,
} from '@bynder-studio/render-core';
import { type PlaybackManipulationRenderer } from '@bynder-studio/render-web';
import { List, Thumbnail, Flex, token, notify } from '@bynder/design-system';
import { IconArrowLeft, IconTextStyle, IconArrowRight } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { parseSrt } from './parseSrt';
import TranscriptGenerateModal from './TranscriptGenerateModal';
import TranscriptStyles from './TranscriptStyles';
import TranscriptForm from './TranscriptForm';
import { getCreative } from 'packages/store/creatives/creatives.selectors';
import { getElementsFonts, getFonts } from 'packages/store/user/user.selectors';
import useForceUpdate from 'packages/hooks/useForceUpdate';
import { getDefaultFontId } from 'packages/pages/editor/utils/getDefaultFontId';
import { StyledDivider } from './Component.styled';
import transcribeService from 'packages/services/transcribeService';
import { prepareDataForTranscribe } from 'packages/common/editor/editor.bloc';

const TRANSCRIBE_TIMEOUT_MS = 300_000; // 5 minutes

const NO_AUDIO_ERROR = 'NO_AUDIO_ELEMENTS';

const isNoAudioMessage = (message: unknown): boolean =>
    typeof message === 'string' && message.toLowerCase().includes('no audio');

const hasEnabledAudioSource = (creativeModel: MultiPageVideoModel): boolean => {
    const currentModel = creativeModel.getCurrentModel?.();

    if (!currentModel) {
        return false;
    }

    const globalAudioHasSource = Boolean(
        creativeModel.getGlobalAudioTrack1?.()?.src || creativeModel.getGlobalAudioTrack2?.()?.src,
    );

    if (globalAudioHasSource) {
        return true;
    }

    const elements = currentModel.getAllElementsRecursively?.() || currentModel.getElements?.() || [];

    return elements.some((element: unknown) => {
        if (element instanceof AudioElement) {
            return true;
        }

        if (element instanceof VideoElement) {
            return element.useAudio === true;
        }

        return false;
    });
};

const waitForTranscribeCompleted = (jobId: number, signal: AbortSignal): Promise<void> =>
    new Promise((resolve, reject) => {
        if (signal.aborted) {
            reject(new DOMException('Aborted', 'AbortError'));

            return;
        }

        let cleanupListener: (() => void) | null = null;

        const timer = setTimeout(() => {
            finish();
            reject(new Error('TRANSCRIBE_TIMEOUT'));
        }, TRANSCRIBE_TIMEOUT_MS);

        function finish() {
            clearTimeout(timer);
            cleanupListener?.();
            signal.removeEventListener('abort', onAbort);
        }

        function onAbort() {
            finish();
            reject(new DOMException('Aborted', 'AbortError'));
        }

        signal.addEventListener('abort', onAbort);

        cleanupListener = transcribeService.listenToTranscribeStatus(
            jobId,
            (status) => {
                if (status === 'COMPLETED') {
                    finish();
                    resolve();
                } else if (status === 'RENDER_FAILED' || status === 'TRANSCRIPTION_FAILED') {
                    finish();
                    reject(new Error(status));
                }
            },
            (error) => {
                finish();
                reject(error);
            },
        );
    });

const fetchSrtFromTranscribeJob = async (jobId: number): Promise<string> => {
    const response = await transcribeService.getTranscribeUrl(jobId);

    if (response.status >= 400 || !response.json?.signedUrl) {
        throw new Error('TRANSCRIBE_URL_MISSING');
    }

    const srtResponse = await fetch(response.json.signedUrl);

    if (!srtResponse.ok) {
        throw new Error('FAILED_TO_FETCH_TRANSCRIBE_SRT');
    }

    const srt = await srtResponse.text();

    if (!srt.trim()) {
        throw new Error('TRANSCRIBE_RESULT_EMPTY');
    }

    return srt;
};

type TranscriptContext = 'editor' | 'content-configurator';

export const TranscriptListItem = ({
    creativeModel,
    onClick,
    context = 'editor',
    isDisabled = false,
}: {
    creativeModel: MultiPageVideoModel;
    onClick: () => void;
    context?: TranscriptContext;
    isDisabled?: boolean;
}) => {
    const { translate } = useTranslate();

    const subtext = useMemo(() => {
        if (context === 'content-configurator') {
            return;
        }

        return creativeModel?.getTranscriptElement?.().enabled
            ? translate('editor.transcript.subtext.enabled')
            : translate('editor.transcript.subtext.disabled');
    }, [context, translate, creativeModel]);

    return (
        <List.Item
            thumbnail={<Thumbnail variant="clean" icon={<IconTextStyle />} />}
            onClick={onClick}
            subtext={subtext}
            rightElements={<IconArrowRight />}
            isDisabled={isDisabled}
        >
            {translate('editor.transcript.title')}
        </List.Item>
    );
};

export const TranscriptBackButton = ({ onBack }: { onBack: () => void }) => {
    const { translate } = useTranslate();

    return (
        <List>
            <List.Item
                onClick={onBack}
                thumbnail={
                    <Flex alignItems="center">
                        <StyledBackIcon />
                        <Thumbnail variant="clean" icon={<IconTextStyle />} />
                    </Flex>
                }
            >
                {translate('editor.transcript.title')}
            </List.Item>
        </List>
    );
};

const Transcript = ({
    creativeModel,
    onBack,
    showEnabledToggle = true,
    showStyles = true,
    showBackButton = true,
    context = 'editor',
    variationPageId,
    variationId,
    pageIndex,
    isInitialTemplateTranscriptEnabled = false,
    onSaveVariation = null,
    manipulationRenderer,
}: {
    creativeModel: MultiPageVideoModel;
    onBack: () => void;
    showEnabledToggle?: boolean;
    showStyles?: boolean;
    showBackButton?: boolean;
    context?: TranscriptContext;
    variationPageId?: string;
    variationId?: string;
    pageIndex?: number;
    isInitialTemplateTranscriptEnabled?: boolean;
    onSaveVariation?: (
        variationId: string,
        callback?: () => void,
        resetPages?: number[],
    ) => Promise<string | undefined>;
    manipulationRenderer?: PlaybackManipulationRenderer | null;
}) => {
    const { translate } = useTranslate();
    const [cues, setCues] = useState<TranscriptCue[]>([]);
    const [syncToken, setSyncToken] = useState(0);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const generateAbortRef = useRef<AbortController | null>(null);
    const fonts = useSelector(getFonts);
    const elementsFonts = useSelector(getElementsFonts);
    const design = useSelector(getCreative);
    const forceUpdate = useForceUpdate();
    const transcriptElement = creativeModel.getTranscriptElement();

    const effectiveTranscriptEnabled = transcriptElement.enabled || isInitialTemplateTranscriptEnabled;
    const enabled = showEnabledToggle ? effectiveTranscriptEnabled : true;
    const shouldEnableTranscriptFromTemplate = isInitialTemplateTranscriptEnabled && !transcriptElement.enabled;

    useEffect(() => {
        if (shouldEnableTranscriptFromTemplate) {
            creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
                enabled: true,
            });
        }
    }, [creativeModel, shouldEnableTranscriptFromTemplate]);

    // Hydrate cues from the model whenever the active variation/page context changes.
    useEffect(() => {
        setCues(creativeModel.getTranscriptElement().cues ?? []);
    }, [creativeModel, variationPageId, pageIndex, transcriptElement.cues]);

    useEffect(() => {
        const eventEmitter = creativeModel.getEventEmitter?.();

        if (!eventEmitter) {
            return;
        }

        const handleSnapshotRestore = () => {
            setCues(creativeModel.getTranscriptElement().cues ?? []);
            setSyncToken((current) => current + 1);
        };

        eventEmitter.on('undoRedoSnapshotRestore', handleSnapshotRestore);

        return () => {
            eventEmitter.off('undoRedoSnapshotRestore', handleSnapshotRestore);
        };
    }, [creativeModel]);

    const hasTranscribableMedia = useMemo(() => {
        const currentModel = creativeModel.getCurrentModel?.();

        if (!currentModel) {
            return false;
        }

        const elements = currentModel.getAllElementsRecursively?.() || currentModel.getElements?.() || [];

        return elements.some((element) => {
            return element instanceof VideoElement || element instanceof AudioElement;
        });
    }, [creativeModel]);

    const defaultFontId = useMemo(() => getDefaultFontId(elementsFonts, fonts), [elementsFonts, fonts]);

    const handleToggleEnabled = useCallback(() => {
        const isEnabling = !enabled;
        const shouldAddFontId = context === 'editor' && isEnabling && transcriptElement.fontId === null;

        creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
            enabled: isEnabling,
            ...(shouldAddFontId ? { fontId: defaultFontId } : {}),
        });
        forceUpdate();
    }, [creativeModel, context, enabled, transcriptElement?.fontId, defaultFontId, forceUpdate]);

    useEffect(
        () => () => {
            generateAbortRef.current?.abort();
        },
        [],
    );

    const handleOpenGenerateModal = useCallback(() => {
        setShowGenerateModal(true);
    }, []);

    const handleCancelGenerate = useCallback(() => {
        generateAbortRef.current?.abort();
        setIsGenerating(false);
        setShowGenerateModal(false);
    }, []);

    const currentPageMeta = creativeModel.getModelsMetaData()[creativeModel.getCurrentPageIndex()];
    const pageName = currentPageMeta?.name || '';
    const designName = design?.name || '';

    const handleCuesChange = useCallback(
        (newCues: TranscriptCue[]) => {
            setCues(newCues);

            creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
                enabled: true,
                cues: newCues,
                fontId: transcriptElement.fontId ?? defaultFontId,
            });
        },
        [creativeModel, defaultFontId, transcriptElement.fontId],
    );

    const handleSeekToCue = useCallback(
        (cue: TranscriptCue) => {
            if (!manipulationRenderer || manipulationRenderer.getIsPlaying()) {
                return;
            }

            const playbackDuration = creativeModel.getPlaybackDuration();
            const startFrame = srtTimecodeToFrame(cue.startTime, playbackDuration.getFrameRate());
            const lastFrame = Math.max(0, playbackDuration.getDuration() - 1);
            const targetFrame = Math.max(0, Math.min(startFrame, lastFrame));

            manipulationRenderer.setCurrentFrame(targetFrame);
        },
        [manipulationRenderer, creativeModel],
    );

    const handleGenerate = useCallback(
        async (languageCode: string | null) => {
            const controller = new AbortController();
            generateAbortRef.current = controller;
            setIsGenerating(true);

            try {
                const rawElements = SpecificationExporter.exportElements(creativeModel.getCurrentModel().getElements());
                const { elements } = prepareDataForTranscribe(rawElements);
                const duration = creativeModel.getPlaybackDuration().duration;
                let createJobResponse = null;

                if (context === 'editor') {
                    const jobData: { elements: unknown[]; duration: number; languageCode?: string } = {
                        elements,
                        duration,
                    };

                    if (languageCode) {
                        jobData.languageCode = languageCode;
                    }

                    createJobResponse = await transcribeService.createTranscribeJob(jobData);
                } else if (variationPageId && variationId) {
                    // Saving regenerates the variation and produces a NEW per-page
                    // variation id. Re-read the fresh id returned by the save instead
                    // of the stale `variationPageId` prop captured in this closure.
                    const savedVariationPageId = await onSaveVariation(variationId);

                    if (!savedVariationPageId) {
                        throw new Error('VARIATION_ID_IS_MISSING');
                    }

                    const jobData: { variationId: string; languageCode?: string } = {
                        variationId: savedVariationPageId,
                    };

                    if (languageCode) {
                        jobData.languageCode = languageCode;
                    }

                    createJobResponse = await transcribeService.createVariationTranscribeJob(jobData);
                }

                if (!createJobResponse) {
                    throw new Error('VARIATION_ID_IS_MISSING');
                }

                if (createJobResponse.status >= 400 || !createJobResponse.json?.id) {
                    if (isNoAudioMessage(createJobResponse.json?.message)) {
                        throw new Error(NO_AUDIO_ERROR);
                    }

                    throw new Error('FAILED_TO_CREATE_TRANSCRIBE_JOB');
                }

                const jobId = createJobResponse.json.id;

                await waitForTranscribeCompleted(jobId, controller.signal);

                const resolvedSrt = await fetchSrtFromTranscribeJob(jobId);

                if (controller.signal.aborted) {
                    return;
                }

                const parsedCues = parseSrt(resolvedSrt);

                if (!parsedCues.length) {
                    throw new Error('TRANSCRIBE_RESULT_EMPTY');
                }

                setCues(parsedCues);
                creativeModel.updateGlobalProperty(ElementTypes.TRANSCRIPT, {
                    enabled: true,
                    cues: parsedCues,
                    fontId: defaultFontId,
                });

                setShowGenerateModal(false);
            } catch (error) {
                if (controller.signal.aborted) {
                    return;
                }

                const isNoAudio = error instanceof Error && error.message === NO_AUDIO_ERROR;
                const isAudioMuted = isNoAudio && !hasEnabledAudioSource(creativeModel);

                let descriptionKey = 'editor.transcript.generate.error.description';

                if (isAudioMuted) {
                    descriptionKey = 'editor.transcript.generate.error.audio-off';
                } else if (isNoAudio) {
                    descriptionKey = 'editor.transcript.generate.error.no-audio';
                }

                notify({
                    title: translate('editor.transcript.generate.error.title'),
                    description: translate(descriptionKey),
                    variant: 'error',
                });
            } finally {
                if (generateAbortRef.current === controller) {
                    generateAbortRef.current = null;
                    setIsGenerating(false);
                }
            }
        },
        [context, creativeModel, defaultFontId, variationPageId, onSaveVariation, variationId, translate],
    );

    return (
        <>
            {showBackButton && <TranscriptBackButton onBack={onBack} />}
            {showEnabledToggle && (
                <List>
                    <List.Item checkedVariant="switch" isChecked={enabled} onClick={handleToggleEnabled}>
                        {translate('editor.transcript.enable')}
                    </List.Item>
                </List>
            )}
            {enabled && (
                <TranscriptForm
                    cues={cues}
                    syncToken={syncToken}
                    isGenerating={isGenerating}
                    designName={designName}
                    pageName={pageName}
                    onGenerate={handleOpenGenerateModal}
                    onCuesChange={handleCuesChange}
                    onSeekToCue={handleSeekToCue}
                    isDisabled={!hasTranscribableMedia}
                />
            )}
            {enabled && showStyles && (
                <>
                    <StyledDivider />
                    <TranscriptStyles creativeModel={creativeModel} />
                </>
            )}
            {showGenerateModal && (
                <TranscriptGenerateModal
                    isGenerating={isGenerating}
                    onGenerate={handleGenerate}
                    onCancel={handleCancelGenerate}
                />
            )}
        </>
    );
};

const StyledBackIcon = styled(IconArrowLeft)`
    margin-right: ${token.spacing3};
`;

export default Transcript;
